/**
 * Pré-análise da modalidade assinatura eletrônica (E74.F1, ADR-0075; E78.F3).
 *
 * Mesma etapa da esteira que a triagem visual — "Análise preliminar" —, mas o
 * trabalho é outro: o escritório transcreve o protocolo da plataforma e manda
 * verificar. O servidor recomputa o código de verificação (SHA-256) de cada
 * peça, confronta com o declarado, confere identidade e IDs, monta a linha do
 * tempo e devolve o quadro. Não há triagem visual nem LR aqui.
 *
 * A tela deixou de ser N cards repetidos na E78.F3 e, desde a E91.F2 (GUS-51
 * RF03), chega no RESULTADO: o estado do caso com a ação de exame, avisos e
 * pendências compactas, o resumo do confronto, a tabela das peças (onde se
 * edita o único dado que é da peça — o código declarado — e se escolhe qual
 * quadro ler), o quadro por eixos da peça aberta e o avanço à mesa. Só depois
 * vêm as áreas de preparação, recolhidas: o protocolo do caso (ADR-0077: ele é
 * do processo, não da peça), o cadastro lido dos documentos e o envio.
 *
 * As fotos do signatário (E80.F1) e a conferência facial (E80.F2) estavam aqui
 * até a E87.F2, foram para a etapa "Análise fotográfica" (GUS-31 CA01) e
 * voltaram na E123.F3 (GUS-128, ADR-0079 E.24): o sistema elege as fotos e faz
 * a conferência sozinho, e a seção "Fotografias e conferência facial" mostra o
 * resultado, com "Trocar fotos" para o perito revisar.
 *
 * O rascunho de cada peça guarda só o que o usuário editou; o valor gravado
 * continua sendo a fonte enquanto ninguém digita, e a gravação bem-sucedida
 * descarta o rascunho. Sem isso, recarregar a lista depois de salvar deixaria
 * na tela o texto antigo por cima do que o servidor acabou de confirmar.
 *
 * E201.F1 (GUS-47, ADR-0144): deixou de ser uma tela. É a área do material na mesa única
 * (`MesaUnicaPage`): documentos e peças, leitura, fotografias e preparação, cada um na sua âncora.
 * O cabeçalho, o estado do exame, o card do pacote e o próximo passo são da mesa, uma vez só. O
 * "Ir para a mesa" saiu, porque a mesa é esta mesma tela. A mesa e a área releem uma à outra:
 * `onGravou` avisa a mesa depois de uma gravação daqui, e `releitura` relê a área quando a mesa
 * mudou. A releitura não avisa de volta, para não virar laço.
 *
 * E203.F5 (ADR-0146): fora da tela e só com pendência. "Protocolo e documentos" (o protocolo, sem
 * "Verificar", e a tabela com o código declarado e o nome) só aparece quando a mesa pede
 * (`protocoloAberto`, pelo "Mais"); o cadastro, a área de preparação e "Documento — CCB i de n" (hash,
 * observações e quadro) saíram, e a observação da peça segue gravada como está; abrir uma peça na
 * tabela mostra o original ao lado; o arquivo único só aparece com a falha da separação; o apoio e o
 * envio ficam no fim, e o "Adicionar documentos" do topo abre o envio (`pedidoDeEnvio`).
 *
 * E205.F1 (ADR-0147): a área desenha os painéis de duas abas da mesa (`PainelDaAba`): Documentos e
 * Fotografias e facial. Os avisos da área (erro, sucesso, processamento) ficam fora das abas, à vista
 * em todas.
 *
 * E205.F4 (ADR-0147 §2.5): a aba Documentos é o envio no alto, o protocolo pedido pelo "Mais" e a
 * lista única (`DocumentosDoCaso`): uma linha por documento, com o kit incompleto e o arquivo único
 * que não separou como linhas, e a leitura do apoio e da imagem de identidade confirmada na linha. A
 * ficha da leitura saiu da mesa ("Mais → Informações da leitura"); fica só a falha técnica da leitura.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LeituraPorImagemEmAndamento } from '@/components/ExameEletronico/LeituraPorImagem';
import { ConferenciaDaLeitura } from '@/components/ConferenciaDaLeitura/ConferenciaDaLeitura';
import { DocumentosDoCaso } from '@/components/Documentos/DocumentosDoCaso';
import type { ProtocoloFormAcao } from '@/components/ExameEletronico/ProtocoloForm';
import { ptBR } from '@/i18n/pt-BR';
import {
  codigosEmTela,
  mensagemDoExecutado,
  pecaDeAbertura,
  resultadoEmAtualizacao,
  secoesIniciaisVazias,
  semChave,
  type PorPeca,
} from '@/pages/PreAnaliseEletronicaDerivados';
import {
  EnvioDeDocumentos,
  ProtocoloEDocumentos,
  secoesIniciais,
  type SecaoDePreparacao,
  type SecoesAbertas,
} from '@/pages/PreAnaliseEletronicaSecoes';
import {
  FalhaDoProcessamento,
  PreAnaliseAvisos,
  dadosDaSecao,
  SecaoDePecas,
} from '@/pages/PreAnaliseEletronicaStates';
import { PainelDaAba } from '@/components/MesaUnica/AbasDaMesa';
import { SecaoDaMesa } from '@/components/MesaUnica/SecaoDaMesa';
import { useProcessamentoInicial } from '@/pages/useProcessamentoInicial';
import { useTrocaDeCaso } from '@/pages/useTrocaDeCaso';
import { PainelDasFotografias } from '@/pages/PreAnaliseEletronicaPaineis';
import { messageFor } from '@/pages/PreAnaliseEletronicaMensagens';
import { useApiClient } from '@/lib/api';
import { usePainelDoDocumento } from '@/lib/painelDoDocumento';
import type { FotografiasDoCaso as LeituraDasFotografias } from '@/lib/useFotografiasDoCaso';
import { renomearDocumento } from '@/lib/case_source_documents';
import {
  listExamesEletronicos,
  protocoloDoCaso,
  salvarCodigoDaPeca,
  salvarProtocoloDoCaso,
  verificarCaso,
  type ExameEletronicoCasoData,
  type ProtocoloAssinaturaData,
} from '@/lib/exame_eletronico';
import { pecasComCodigoAlterado } from '@/lib/exame_eletronico_edicao';
import { codigoValido, normalizarCodigo } from '@/lib/exame_eletronico_peca';
import { resumoDasConsultas, type ResumoDasConsultas } from '@/lib/exame_eletronico_processamento';
import type { ArquivoPendenteData } from '@/lib/arquivos_pendentes';

const t = ptBR.exameEletronico;

export interface PreAnaliseEletronicaPageProps {
  caseId: string;
  /** Muda quando a mesa gravou algo: a área relê o caso (sem avisar de volta). */
  releitura?: unknown;
  /** Uma gravação daqui terminou e foi relida: a mesa relê a parte dela. */
  onGravou?: () => void;
  /** O caso está sendo verificado ou processado: o estado do exame da mesa diz que espera. */
  onProcessando?: (emAtualizacao: boolean) => void;
  /** E203.F5.T02: "Protocolo e documentos" à vista (o "Mais" do topo pediu). */
  protocoloAberto?: boolean;
  onFecharProtocolo?: () => void;
  /** E203.F5.T01: muda quando o "Adicionar documentos" do topo pede o envio. */
  pedidoDeEnvio?: number;
  /** E203.F5.T07: a leitura das fotografias que a mesa já faz; sem ela, a seção lê a sua. */
  fotografias?: LeituraDasFotografias;
  /** E206.F2 (GUS-296 item 2): o que falta ao caso digital; a comparação facial espera. */
  arquivosPendentes?: readonly ArquivoPendenteData[] | undefined;
}

export function PreAnaliseEletronicaPage({
  caseId,
  releitura,
  onGravou,
  onProcessando,
  protocoloAberto = false,
  onFecharProtocolo,
  pedidoDeEnvio = 0,
  fotografias,
  arquivosPendentes,
}: PreAnaliseEletronicaPageProps): JSX.Element {
  const client = useApiClient();
  const documentoAoLado = usePainelDoDocumento();
  // Em ref: o aviso da mesa não entra nas dependências das ações, que ficam estáveis.
  const avisarAMesa = useRef(onGravou);
  avisarAMesa.current = onGravou;
  const [caso, setCaso] = useState<ExameEletronicoCasoData | null>(null);
  const [erroGlobal, setErroGlobal] = useState<string | null>(null);
  const [sucessoGlobal, setSucessoGlobal] = useState<string | null>(null);
  // E91.F3 (RF09): o que as consultas externas fizeram no processamento que
  // acabou de anunciar — lido dos quadros devolvidos, por fonte.
  const [consultas, setConsultas] = useState<ResumoDasConsultas | null>(null);
  const [acaoDoCaso, setAcaoDoCaso] = useState<ProtocoloFormAcao | null>(null);
  const [salvandoPeca, setSalvandoPeca] = useState<string | null>(null);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [codigosEditados, setCodigosEditados] = useState<PorPeca<string>>({});
  const [erroPorPeca, setErroPorPeca] = useState<PorPeca<string>>({});
  // E90.F6: a renomeação em voo é do caso (F-E90.F6-01): zera na troca, e a
  // resposta tardia de A não a devolve — senão B fica com o controle travado.
  const [renomeando, setRenomeando] = useState<string | null>(null);
  // E91.F2 (RF03): quais áreas de preparação estão abertas. `null` até a
  // primeira carga — é ela que diz o que ainda é a próxima ação.
  const [secoes, setSecoes] = useState<SecoesAbertas | null>(null);
  const [documentosAdicionados, setDocumentosAdicionados] = useState(0);
  const refDocumentos = useRef<HTMLDivElement>(null);
  // F-E89.F2-01: a rota troca o caso sem remontar a página. Nada do caso
  // anterior sobrevive à troca, e nada que chegue tarde dele escreve aqui.
  const casoVigente = useTrocaDeCaso(caseId, () => {
    setCaso(null);
    setSecoes(null);
    setErroGlobal(null);
    setSucessoGlobal(null);
    setConsultas(null);
    setAcaoDoCaso(null);
    setSalvandoPeca(null);
    setRenomeando(null);
    setSelecionada(null);
    setCodigosEditados({});
    setErroPorPeca({});
  });

  /**
   * Relê o gravado. Só a leitura mais RECENTE escreve (E89.F2): o documento
   * adicionado dispara uma leitura e, logo depois, o automático — e a resposta
   * lenta da primeira não pode pintar por cima do quadro que ele acabou de apurar.
   */
  const leituras = useRef(0);
  const recarregar = useCallback(async (): Promise<void> => {
    leituras.current += 1;
    const esta = leituras.current;
    // A mais recente do caso ERRADO também é a mais recente (F-E89.F2-01): a
    // leitura só escreve se continua sendo a última e se a tela ainda é do caso dela.
    const valida = (): boolean => esta === leituras.current && casoVigente.current === caseId;
    try {
      const lista = await listExamesEletronicos(client, caseId);
      if (valida()) setCaso(lista);
    } catch (err) {
      if (valida()) throw err;
    }
  }, [client, caseId, casoVigente]);

  /**
   * O servidor gravou (importação do comprovante, documento novo): a tela vai
   * buscar o gravado. Se a recarga falha, o erro aparece e o estado anterior
   * fica — a tela não inventa o que não conseguiu ler (F-E83.F1-01).
   */
  const recarregarOuAvisar = useCallback((): void => {
    void recarregar().catch((err: unknown) => setErroGlobal(messageFor(err)));
  }, [recarregar]);

  /** Gravou aqui (leitura, cadastro, documento): relê a área e avisa a mesa. */
  const gravouAqui = useCallback((): void => {
    recarregarOuAvisar();
    avisarAMesa.current?.();
  }, [recarregarOuAvisar]);

  // E201.F1: a mesa gravou (conclusão, pacote, banco): a área relê o caso. A primeira passagem é
  // a carga inicial, que o efeito abaixo já faz.
  const primeiraReleitura = useRef(true);
  useEffect(() => {
    if (primeiraReleitura.current) {
      primeiraReleitura.current = false;
      return;
    }
    recarregarOuAvisar();
  }, [releitura, recarregarOuAvisar]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const lista = await listExamesEletronicos(client, caseId);
        if (cancelled) return;
        setCaso(lista);
        setSelecionada((atual) => atual ?? pecaDeAbertura(lista.pecas));
        setSecoes((atuais) => atuais ?? secoesIniciais(lista));
      } catch (err) {
        if (!cancelled) setErroGlobal(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, caseId]);

  const {
    falha: falhaDoProcessamento,
    solicitar: solicitarProcessamento,
    lendoImagem,
  } = useProcessamentoInicial({
    client,
    caseId,
    pronto: caso !== null,
    acaoDoCaso,
    setAcaoDoCaso,
    recarregar,
    mensagemDoErro: messageFor,
    onErroDeLeitura: setErroGlobal,
    onExecutado: (resultado) => {
      setSucessoGlobal(mensagemDoExecutado(resultado));
      setConsultas(resumoDasConsultas(resultado.exames));
    },
  });
  const emAtualizacao = resultadoEmAtualizacao(acaoDoCaso);
  useEffect(() => onProcessando?.(emAtualizacao), [emAtualizacao, onProcessando]);

  // A ação de caso terminou (processamento, verificação, protocolo), com sucesso ou não: a área
  // já releu, e a mesa relê a parte dela — o processamento relê sempre, mesmo calado ou depois
  // de uma falha, porque o servidor pode ter gravado (E82.F5, E89.F2.C02).
  const acaoAnterior = useRef<ProtocoloFormAcao | null>(null);
  useEffect(() => {
    if (acaoAnterior.current !== null && acaoDoCaso === null) avisarAMesa.current?.();
    acaoAnterior.current = acaoDoCaso;
  }, [acaoDoCaso]);

  /** Documento novo: relê para ele aparecer, e rearma o automático (decisão do owner). */
  const documentoAdicionado = useCallback((): void => {
    setDocumentosAdicionados((n) => n + 1); // E124.F1: a seção de fotografias relê
    gravouAqui();
    solicitarProcessamento();
  }, [gravouAqui, solicitarProcessamento]);

  const pecas = useMemo(() => caso?.pecas ?? [], [caso]);
  const algumaConcluida = pecas.some((p) => p.exame?.status === 'concluido');

  const codigos = useMemo(() => codigosEmTela(pecas, codigosEditados), [pecas, codigosEditados]);

  /**
   * Peças com código digitado e ainda não gravado (GUS-45 RF01).
   *
   * Trava o `Verificar`: o motor roda sobre o que está GRAVADO, então deixá-lo
   * rodar com a tabela mostrando outro valor devolvia um selo calculado sobre
   * o código antigo ao lado do código novo. Observação não entra na conta — ela
   * não invalida verificação nenhuma (RN03).
   */
  const pecasPendentes = useMemo(() => pecasComCodigoAlterado(pecas, codigos), [pecas, codigos]);

  /**
   * Uma ação de caso por vez: o card trava enquanto está em voo, mas o guard
   * fica aqui também — é a página que conhece o estado, não o botão.
   */
  const executarNoCaso = useCallback(
    async (acao: ProtocoloFormAcao, chamada: () => Promise<unknown>, sucesso: string | null) => {
      if (acaoDoCaso !== null) return;
      setAcaoDoCaso(acao);
      setErroGlobal(null);
      setSucessoGlobal(null);
      setConsultas(null);
      let erro: string | null = null;
      try {
        await chamada();
        await recarregar();
      } catch (err) {
        erro = messageFor(err);
      }
      // A ação que termina depois da troca de caso não solta a ação do caso novo
      // nem pinta aviso nele (F-E89.F2-01).
      if (casoVigente.current !== caseId) return;
      setAcaoDoCaso(null);
      if (erro !== null) setErroGlobal(erro);
      else if (sucesso !== null) setSucessoGlobal(sucesso);
    },
    [acaoDoCaso, caseId, casoVigente, recarregar],
  );

  const handleSalvarProtocolo = (protocolo: ProtocoloAssinaturaData): void => {
    void executarNoCaso(
      'salvando',
      () => salvarProtocoloDoCaso(client, caseId, protocoloDoCaso(protocolo)),
      t.protocoloSalvo,
    );
  };

  /** Mesma verificação, refazendo as consultas às fontes externas (gate de custo). */
  const handleAtualizarConsultas = (): void => {
    void executarNoCaso(
      'atualizando',
      () => verificarCaso(client, caseId, { atualizarConsultas: true }),
      null,
    );
  };

  // E90.F6: o nome do documento é gravado no documento (não no exame) e a
  // listagem é relida — a mesma disciplina de troca de caso do código declarado.
  const handleRenomear = (docId: string, nome: string | null): void => {
    if (renomeando !== null) return;
    const alvo = caseId;
    void (async () => {
      setRenomeando(docId);
      setErroPorPeca((atuais) => semChave(atuais, docId));
      let erro: string | null = null;
      try {
        await renomearDocumento(client, docId, nome);
        await recarregar();
      } catch (err) {
        erro = messageFor(err);
      }
      if (casoVigente.current !== alvo) return;
      setRenomeando(null);
      if (erro !== null) setErroPorPeca((atuais) => ({ ...atuais, [docId]: erro }));
      else avisarAMesa.current?.();
    })();
  };

  const handleSalvarPeca = (docId: string): void => {
    if (salvandoPeca !== null) return;
    const peca = pecas.find((p) => p.source_document_id === docId);
    if (peca === undefined) return;
    const codigo = codigos[docId] ?? '';
    if (!codigoValido(codigo)) {
      setErroPorPeca((atuais) => ({ ...atuais, [docId]: t.form.hashDeclaradoErro }));
      return;
    }
    const normalizado = normalizarCodigo(codigo);
    // E203.F5.T03: a observação da peça saiu da tela; segue gravada como está.
    const observacoes = (peca.observacoes_peca ?? '').trim();
    const alvo = caseId;
    void (async () => {
      setSalvandoPeca(docId);
      setErroPorPeca((atuais) => semChave(atuais, docId));
      let erro: string | null = null;
      try {
        await salvarCodigoDaPeca(client, alvo, docId, {
          codigo_declarado: normalizado.length > 0 ? normalizado : null,
          observacoes: observacoes.length > 0 ? observacoes : null,
        });
        await recarregar();
      } catch (err) {
        erro = messageFor(err);
      }
      if (casoVigente.current !== alvo) return; // F-E89.F2-01
      setSalvandoPeca(null);
      if (erro !== null) {
        setErroPorPeca((atuais) => ({ ...atuais, [docId]: erro }));
        return;
      }
      // O gravado volta a ser a fonte: o rascunho desta peça deixa de existir.
      setCodigosEditados((atuais) => semChave(atuais, docId));
      setSucessoGlobal(t.codigoDaPecaSalvo);
      avisarAMesa.current?.();
    })();
  };

  // Identidade estável: um callback novo a cada render reagendaria o efeito da
  // conferência sem parar (lição do mock instável que virava laço).
  const editarCodigo = useCallback((docId: string, codigo: string): void => {
    setCodigosEditados((atuais) => ({ ...atuais, [docId]: codigo }));
  }, []);

  /**
   * Joga fora o rascunho da peça (GUS-45 RF03/CA03).
   *
   * Nada vai ao servidor: o gravado nunca foi tocado. Some o rascunho do código
   * e o do texto, e com eles o erro e o aviso de sucesso daquela linha — que
   * falavam de uma edição que deixou de existir.
   */
  const descartarPeca = useCallback((docId: string): void => {
    setCodigosEditados((atuais) => semChave(atuais, docId));
    setErroPorPeca((atuais) => semChave(atuais, docId));
  }, []);

  const alternarSecao = useCallback((secao: SecaoDePreparacao): void => {
    setSecoes((atuais) => ({ ...(atuais ?? secoesIniciaisVazias()), [secao]: !atuais?.[secao] }));
  }, []);

  /**
   * A pendência de kit aponta para a ação que existe (RF08): abre o envio e
   * rola até ele. `scrollIntoView` é opcional porque o jsdom não o implementa.
   */
  const abrirDocumentos = useCallback((): void => {
    setSecoes((atuais) => ({ ...(atuais ?? secoesIniciaisVazias()), documentos: true }));
    requestAnimationFrame(() => {
      refDocumentos.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // E203.F5.T01: o "Adicionar documentos" do topo abre o envio e rola até ele.
  useEffect(() => {
    if (pedidoDeEnvio > 0) abrirDocumentos();
  }, [pedidoDeEnvio, abrirDocumentos]);

  /** E203.F5.T03: abrir uma peça na tabela mostra o original no painel ao lado. */
  const abrirPeca = useCallback(
    (docId: string): void => {
      setSelecionada(docId);
      documentoAoLado?.abrir({ documentoId: docId, pagina: 1 });
    },
    [documentoAoLado],
  );
  // E205.F4: a leitura confirmada na linha do documento relê as fotografias e rearma o automático.
  const leituraConfirmada = useCallback((): void => {
    void fotografias?.recarregar().catch(() => undefined);
    gravouAqui();
    solicitarProcessamento();
  }, [fotografias, gravouAqui, solicitarProcessamento]);

  return (
    <div
      data-testid="pre-analise-page"
      data-modalidade="eletronica"
      data-acao-do-caso={acaoDoCaso ?? 'nenhuma'}
    >
      <PreAnaliseAvisos
        erroGlobal={erroGlobal}
        sucessoGlobal={sucessoGlobal}
        consultas={consultas}
        caso={caso}
      />

      <FalhaDoProcessamento
        mensagem={falhaDoProcessamento}
        ocupado={acaoDoCaso !== null}
        onTentarDeNovo={solicitarProcessamento}
      />

      <LeituraPorImagemEmAndamento ativa={lendoImagem} />

      <PainelDaAba aba="documentos">
        {/* E205.F4.T04 (ADR-0147 §2.5): o envio no alto da aba. */}
        {caso !== null ? (
          <EnvioDeDocumentos
            ref={refDocumentos}
            caseId={caseId}
            algumaConcluida={algumaConcluida}
            abertas={secoes}
            onAlternar={alternarSecao}
            onDocumentoAdicionado={documentoAdicionado}
          />
        ) : null}

        {protocoloAberto && caso !== null ? (
          <ProtocoloEDocumentos
            caseId={caseId}
            caso={caso}
            acaoDoCaso={acaoDoCaso}
            algumaConcluida={algumaConcluida}
            edicaoPendenteDePecas={pecasPendentes.length > 0}
            onSalvarProtocolo={handleSalvarProtocolo}
            onAtualizarConsultas={handleAtualizarConsultas}
            onFechar={() => onFecharProtocolo?.()}
          >
            {pecas.length > 0 ? (
              <SecaoDePecas
                pecas={pecas}
                {...dadosDaSecao(caso)}
                selecionada={selecionada}
                rascunhos={codigos}
                salvando={salvandoPeca}
                erroPorPeca={erroPorPeca}
                onSelecionar={abrirPeca}
                onEditarCodigo={editarCodigo}
                onSalvarPeca={handleSalvarPeca}
                onDescartarPeca={descartarPeca}
                onRenomear={handleRenomear}
                renomeando={renomeando}
                emAtualizacao={emAtualizacao}
              />
            ) : null}
          </ProtocoloEDocumentos>
        ) : null}

        {/* E205.F4.T02: um documento por linha, com o kit incompleto e o arquivo único que não
            separou como linhas; a leitura do apoio e da imagem de identidade se confirma na linha. */}
        <DocumentosDoCaso
          client={client}
          caseId={caseId}
          caso={caso}
          emProcessamento={emAtualizacao}
          concluido={algumaConcluida}
          leituras={fotografias?.dados?.candidatas.leituras ?? []}
          onConfirmado={leituraConfirmada}
          onEnviar={abrirDocumentos}
        />

        {/* A falha técnica da leitura (o resto da leitura está em "Mais → Informações da leitura"). */}
        <SecaoDaMesa id="leitura">
          <ConferenciaDaLeitura client={client} caseId={caseId} releitura={caso} soComPendencia />
        </SecaoDaMesa>
      </PainelDaAba>

      <PainelDasFotografias
        caseId={caseId}
        concluido={algumaConcluida}
        releitura={documentosAdicionados}
        onEntradaMudou={solicitarProcessamento}
        fotografias={fotografias}
        arquivosPendentes={arquivosPendentes}
      />
    </div>
  );
}
